export default {
  nav: {
    home: "Home",
    login: "Log in",
    logout: "Log out",
    createProject: "New project",
  },
  hero: {
    name: "Sergio Penagos",
    role: "Software Engineer · Design Systems · Bilingual (EN/ES)",
    bio: "Software engineer with professional experience building scalable, responsive and accessible web applications for banking, travel and healthcare. I lead frontend architecture with React, Vue, TypeScript and scalable Design Systems, and I ship faster by weaving AI-assisted workflows (Claude Code, Copilot, Gemini) into my daily engineering — without lowering the quality bar. Bilingual English/Spanish, experienced coordinating distributed teams and mentoring junior developers.",
    seeCv: "See CV",
    viewWork: "View my work",
    hireMe: "Hire me",
  },
  timeline: {
    heading: "About me",
    scrollHint: "Scroll to explore the full journey",
    events: {
      degree2026: {
        date: "Jul 2026",
        title: "Graduated — Software Engineering",
        subtitle: "Degree completed",
        points: [
          "Capped years of shipping production software with formal engineering fundamentals.",
        ],
      },
      globant: {
        date: "Currently",
        title: "Globant",
        subtitle: "Software Engineer · Project Sentinel (Edenred)",
        points: [
          "Building Sentinel: an onboarding platform where companies applying to work with Edenred upload documents for preview, visualization and automated analysis.",
          "Security screening workflows: legal-signature validation plus FBI, police, CIA and Interpol background checks.",
          "Built with .NET + React using Hexagonal Architecture, Azure AI, containers and Kubernetes.",
          "Collaborated on SEO and performance improvements; AI-assisted development in the daily workflow.",
        ],
      },
      britishCouncil: {
        date: "2024 – 2025",
        title: "British Council",
        subtitle: "Software Engineer · Frontend (React/TypeScript)",
        points: [
          "Delivered scalable React + TypeScript features in a fully bilingual (EN/ES) team.",
          "Designed a cross-functional incident resolution process between L2 and L3 teams, doubling the successful resolution rate.",
          "Monitored production health with dashboards (Datadog) and coordinated complex incident analysis.",
          "Mentored junior developers (SENA trainees).",
        ],
      },
      helitours: {
        date: "Aug 2024 – Nov 2024",
        title: "Helitours · Freelance",
        subtitle: "Senior Web Developer",
        points: [
          "Led the frontend of the company's new website on WordPress.",
          "Designed and customized themes for responsive, cross-device compatibility.",
          "Introduced structured Scrum practices working directly with stakeholders.",
        ],
      },
      mha: {
        date: "Aug 2024 – Nov 2024",
        title: "Hemodynamic Monitoring System (MHA)",
        subtitle: "Senior Web Developer · HealthTech",
        points: [
          "Designed a modular monolith with Node.js, React and TypeScript.",
          "Integrated medical-device data (custo-30/custo-310) into real-time visualizations.",
          "Implemented role-based access control and full CRUD for users, devices, hospitals and staff.",
          "Developed PDF generation and reporting features.",
        ],
      },
      medellin: {
        date: "Jan 2024 – Aug 2024",
        title: "Medellín Joven",
        subtitle: "Senior Web Developer",
        points: [
          "Designed the frontend architecture with Angular and TypeScript.",
          "Improved backend data handling with Node.js and PostgreSQL.",
          "Agile delivery for a scalable platform serving young citizens.",
        ],
      },
      vass: {
        date: "Jul 2022 – Dec 2023",
        title: "VASS",
        subtitle: "V2 → V1 Developer · Banking & Fintech",
        points: [
          "Shipped INTERCAM, a banking compliance platform, with Vue.js + TypeScript.",
          "Built MIFEL private banking site: cards, checkbooks, clarifications, movements and DIMO connectivity.",
          "Developed CFG DANDO public and onboarding experiences.",
          "Storybook-driven custom UI library for international multi-language teams.",
        ],
      },
      senaAnalyst: {
        date: "Apr 2020 – Jul 2022",
        title: "Graduated from SENA",
        subtitle: "Technologist in Software Analysis & Development",
        points: [
          "Associate degree focused on software analysis, development and information systems.",
        ],
      },
      dolphin: {
        date: "Mar 2021 – Jun 2021",
        title: "Dolphin Ingeniería S.A.S",
        subtitle: "Web Developer",
        points: [
          "Developed Kairos, a .NET web app managing PRIMAX projects in the hydrocarbon industry.",
          "Frontend work with JavaScript, TypeScript, CSS and Sass.",
        ],
      },
      qoopa: {
        date: "Dec 2019 – Mar 2021",
        title: "Qoopa",
        subtitle: "Website Developer",
        points: [
          "Implemented Scrum methodology across the team.",
          "Created components for a custom UI library with React Native, Vue.js and Styled Components.",
          "Built interactive websites with Laravel and modern JavaScript.",
        ],
      },
      soul: {
        date: "Apr 2019 – Nov 2019",
        title: "Grupo Soul",
        subtitle: "Website Developer (Internship)",
        points: [
          "Developed web pages with HTML, CSS (Bootstrap 4), JavaScript, jQuery, PHP and AJAX.",
        ],
      },
      senaTech: {
        date: "Oct 2018 – Nov 2019",
        title: "Graduated from SENA",
        subtitle: "Technical Program in Software Development",
        points: [
          "First formal step into software development: fundamentals, web technologies and project practice.",
        ],
      },
      conex: {
        date: "Oct 2017 – Dec 2018",
        title: "Conex Travel",
        subtitle: "Website Developer (Part-time)",
        points: [
          "Built responsive UIs with React.js and Bootstrap.",
          "Created reusable components across projects.",
          "Optimized bundles to cut file sizes and improve load times by 20%.",
        ],
      },
    },
  },
  projects: {
    recentHeading: "My Recent Work",
    otherHeading: "Other projects",
    viewDetails: "View details",
    goToWeb: "Go to site",
    items: {
      intercambank: {
        description:
          "Banking compliance platform built for INTERCAM bank with an international team: public multi-language site on CMS Modyo, custom UI library components and micro-frontend architecture.",
      },
      mifel: {
        description:
          "Private banking site for MIFEL bank: cards, checkbooks, clarifications, movements, checks, debts and credits management, plus DIMO connectivity — multi-language and accessibility-first.",
      },
      dando: {
        description:
          "Public, private and onboarding experiences for CFG DANDO, working closely with multicultural teams across countries on Vue.js and TypeScript.",
      },
      deone: {
        description:
          "Express Courier platform for PriceSmart Colombia (web + mobile): send packages and request services, with a shared component library across platforms.",
      },
      kairos: {
        description:
          ".NET web app for managing PRIMAX projects in the hydrocarbon industry: feature development, bug fixing and general maintenance on the frontend.",
      },
    },
    other: {
      bbc: {
        description:
          "Bogota Beer Company website where clients explore products, offers, services and locations.",
      },
      stella: {
        description:
          "Stella Artois Colombia website: products, offers and brand experience for end customers.",
      },
      google: {
        description:
          "Google landing page clone with dropdown tools and mobile version, built with Vue.",
      },
      weather: {
        description:
          "Weather app that fetches per-city forecasts from a free API using axios and TypeScript.",
      },
    },
  },
  skills: {
    heading: "Skills",
    frontend: "Frontend",
    backend: "Backend & Data",
    tools: "Tools & Platforms",
    ai: "AI-Assisted Engineering",
  },
  services: {
    heading: "Freelance Offers",
    cta: "Let's talk",
    hourly: "/h",
    offers: {
      standard: {
        name: "Software Engineering & Consulting",
        blurb:
          "Full product delivery: frontend architecture, Design Systems, APIs, team leadership and Scrum — everything your project needs.",
      },
      ai: {
        name: "AI-Boosted Delivery",
        blurb:
          "Same scope, same quality — supercharged with Claude Code / Copilot workflows. Want less time? Pay more.",
      },
    },
  },
  contact: {
    heading: "Contact",
    lead: "Let's build something together. Reach out through any channel:",
    email: "Email",
  },
  login: {
    welcome: "Welcome back",
    hint: "Log in to manage portfolio projects",
    username: "Username",
    password: "Password",
    submit: "Log In",
    goBack: "Go back",
    invalid: "Invalid credentials!",
  },
  create: {
    heading: "Create a new project",
    name: "Name",
    namePlaceholder: "Project name",
    description: "Description",
    type: "Project type",
    typePlaceholder: "Pick one",
    url: "URL",
    technologies: "Technologies",
    technologiesPlaceholder: "Pick the stack",
    responsibilities: "Responsibilities",
    tags: "Tags",
    tagsPlaceholder: "Pick some",
    images: "Images (max 10)",
    video: "Video (optional)",
    submit: "Create Project",
    submitting: "Creating…",
    success: "Project created successfully",
    failed: "Failed to create project. Please try again.",
    errors: {
      summary: "Please fix the following:",
      nameRequired: "Project's name is required",
      descriptionRequired: "Project's description is required",
      typeRequired: "Project's type is required",
      linkRequired: "Project's link is required",
      techMin: "At least one technology is required",
      respRequired: "Responsibilities are required",
      tagsMin: "At least one tag is required",
      imagesMax: "Maximum of 10 images allowed",
      imagesMin: "At least one image is required",
    },
  },
  detail: {
    about: 'About "{title}"',
    techStack: "Tech Stack",
    evidence: "Evidence",
    moreDetails: "More details",
    responsibilities: "Responsibilities",
    topics: "Topics",
    goToSite: "Go to site",
    goHome: "Go home",
    notFound: "Project not found",
  },
  footer: {
    name: "Jör — Sergio Penagos",
    rights: "All rights reserved.",
    builtWith: "Built with Vue, Flask and too much coffee.",
  },
};
